<template>
  <ListGroup class="mt-2">
    <ListGroupItem
      v-for="item in visibleChecklistItems"
      :key="item.id"
    >
      <div class="flex min-h-[27px] items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <Checkbox
            v-model="checklist[item.toggleField]"
            :size="item.checkboxSize"
            no-margin
            :readonly="props.isLocked"
          />
          <div>{{ $t(item.labelKey) }}</div>
        </div>
        <div
          v-if="shouldShowAmountInput(item)"
          class="w-[120px] flex-shrink-0"
        >
          <Input
            v-model="checklist[item.amountField!]"
            size="sm"
            :readonly="props.isLocked"
            :icon="props.currencyIcon"
            class="w-full"
            :has-error="getChecklistAmountError(item.amountField)"
          />
        </div>
      </div>
      <div v-if="shouldShowAmountInput(item) && item.amountField">
        <InputError
          class="mt-2"
          :has-error="getChecklistAmountError(item.amountField)"
        >
          {{ getChecklistAmountMessage(item.amountField) }}
        </InputError>
      </div>
    </ListGroupItem>
  </ListGroup>
</template>

<script setup lang="ts">
// vue
import { computed } from 'vue'
// components
import Checkbox from '@/components/forms/Checkbox.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import ListGroup from '@/components/list/ListGroup.vue'
import ListGroupItem from '@/components/list/ListGroupItem.vue'
// types
import type { AppointmentChecklist } from '@/types/domain/appointment'

type ChecklistToggleField =
  | 'settlementFigureGiven'
  | 'vehicleValued'
  | 'testDriveCompleted'
  | 'newCarOffersProvided'
  | 'refinanceQuoteGiven'

type ChecklistAmountField = 'settlementAmount' | 'valuationAmount' | 'refinanceQuoteAmount'

type AppointmentChecklistForm = Pick<AppointmentChecklist, ChecklistToggleField> &
  Record<ChecklistAmountField, string | undefined>

interface ChecklistValidationField {
  $error: boolean
  $errors: Array<{ $message?: string }>
}

interface ChecklistValidationFields {
  settlementAmount: ChecklistValidationField
  valuationAmount: ChecklistValidationField
  refinanceQuoteAmount: ChecklistValidationField
}

interface ChecklistItemConfig {
  id: ChecklistToggleField
  labelKey: string
  toggleField: ChecklistToggleField
  amountField?: ChecklistAmountField
  checkboxSize?: 'sm' | 'md' | 'lg'
  isVisible?: boolean
}

interface AgreementAppointmentChecklistProps {
  validationFields: ChecklistValidationFields
  currencyIcon: string
  isLocked: boolean
  isPhysicalAppointment: boolean
}

const props = defineProps<AgreementAppointmentChecklistProps>()

const checklist = defineModel<AppointmentChecklistForm>('checklist', {
  required: true
})

/**
 * Declarative checklist row config used to render the checklist with `v-for`.
 */
const checklistItems = computed<ChecklistItemConfig[]>(() => [
  {
    id: 'settlementFigureGiven',
    labelKey: 'ui.entities.appointment.checklist.settlementFigureGiven',
    toggleField: 'settlementFigureGiven',
    amountField: 'settlementAmount',
    checkboxSize: 'md'
  },
  {
    id: 'vehicleValued',
    labelKey: 'ui.entities.appointment.checklist.vehicleValued',
    toggleField: 'vehicleValued',
    amountField: 'valuationAmount'
  },
  {
    id: 'testDriveCompleted',
    labelKey: 'ui.entities.appointment.checklist.testDriveCompleted',
    toggleField: 'testDriveCompleted',
    isVisible: props.isPhysicalAppointment
  },
  {
    id: 'newCarOffersProvided',
    labelKey: 'ui.entities.appointment.checklist.newVehicleOffersProvided',
    toggleField: 'newCarOffersProvided'
  },
  {
    id: 'refinanceQuoteGiven',
    labelKey: 'ui.entities.appointment.checklist.refinanceQuoteGiven',
    toggleField: 'refinanceQuoteGiven',
    amountField: 'refinanceQuoteAmount'
  }
])

/**
 * Checklist rows that should currently be shown in the UI.
 */
const visibleChecklistItems = computed<ChecklistItemConfig[]>(() =>
  checklistItems.value.filter(item => item.isVisible !== false)
)

/**
 * Returns whether the current checklist row should render its amount input.
 */
function shouldShowAmountInput(item: ChecklistItemConfig): boolean {
  return Boolean(item.amountField && checklist.value[item.toggleField])
}

/**
 * Returns the validation error state for a checklist amount field.
 */
function getChecklistAmountError(field?: ChecklistAmountField): boolean {
  return field ? props.validationFields[field].$error : false
}

/**
 * Returns the first validation message for a checklist amount field.
 */
function getChecklistAmountMessage(field?: ChecklistAmountField): string | undefined {
  return field ? props.validationFields[field].$errors[0]?.$message : undefined
}
</script>
